import React, { createContext, useContext, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const HomeContext = createContext();

export const useHome = () => useContext(HomeContext);

// Request cancelado (AbortController): propaga sem toast para o efeito ignorar.
const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

export const HomeProvider = ({ children }) => {
  // Entradas recentes -> resposta: { entries: [], start, end }
  const listHomeEntries = useCallback(async (params, config = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];

    try {
      const response = await api.get('internals/internal/async/home/entries/', {
        ...config,
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return Array.isArray(response.data?.entries) ? response.data.entries : [];
    } catch (error) {
      if (isCanceled(error)) throw error;
      console.error('Erro ao buscar entradas recentes da home:', error);
      toast.error('Erro ao buscar entradas recentes');
      return [];
    }
  }, []);

  // Transferencias recentes -> resposta: { transfers: { sender: [], recipient: [] }, start, end }
  const listHomeTransfers = useCallback(async (params, config = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return { recipient: [], sender: [] };

    try {
      const response = await api.get('internals/internal/async/home/transfers/', {
        ...config,
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const transfers = response.data?.transfers || {};
      return {
        recipient: Array.isArray(transfers.recipient) ? transfers.recipient : [],
        sender: Array.isArray(transfers.sender) ? transfers.sender : [],
      };
    } catch (error) {
      if (isCanceled(error)) throw error;
      console.error('Erro ao buscar transferencias da home:', error);
      toast.error('Erro ao buscar transferencias');
      return { recipient: [], sender: [] };
    }
  }, []);

  // Agendamentos do dia -> resposta: { schedulings: [], start, end }
  const listHomeSchedulings = useCallback(async (params, config = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];

    try {
      const response = await api.get('internals/internal/async/home/schedulings/', {
        ...config,
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return Array.isArray(response.data?.schedulings) ? response.data.schedulings : [];
    } catch (error) {
      if (isCanceled(error)) throw error;
      console.error('Erro ao buscar agendamentos da home:', error);
      toast.error('Erro ao buscar agendamentos do dia');
      return [];
    }
  }, []);

  // Quantitativo - custodia -> resposta: { quantitative: { sentenciados, provisorios, temporarios, civis, dia, fechado, semiaberto, aberto } }
  const listHomeQuantitativeCustody = useCallback(async (params, config = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return {};

    try {
      const response = await api.get('internals/internal/async/home/quantitative/custody/', {
        ...config,
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const quantitative = response.data?.quantitative;
      return quantitative && typeof quantitative === 'object' ? quantitative : {};
    } catch (error) {
      if (isCanceled(error)) throw error;
      console.error('Erro ao buscar quantitativo de custodia da home:', error);
      toast.error('Erro ao buscar quantitativo de custodia');
      return {};
    }
  }, []);

  // Quantitativo - operacional -> resposta: { quantitative: { vagas, fugitivo } }
  const listHomeQuantitativeOperational = useCallback(async (params, config = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return {};

    try {
      const response = await api.get('internals/internal/async/home/quantitative/operational/', {
        ...config,
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const quantitative = response.data?.quantitative;
      return quantitative && typeof quantitative === 'object' ? quantitative : {};
    } catch (error) {
      if (isCanceled(error)) throw error;
      console.error('Erro ao buscar quantitativo operacional da home:', error);
      toast.error('Erro ao buscar quantitativo operacional');
      return {};
    }
  }, []);

  // Saidas temporarias -> resposta: { temporary_leavings: [], start, end }
  const listHomeTemporaryLeavings = useCallback(async (params, config = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];

    try {
      const response = await api.get('internals/internal/async/home/temporary_leavings/', {
        ...config,
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return Array.isArray(response.data?.temporary_leavings) ? response.data.temporary_leavings : [];
    } catch (error) {
      if (isCanceled(error)) throw error;
      console.error('Erro ao buscar saidas temporarias da home:', error);
      toast.error('Erro ao buscar saidas temporarias');
      return [];
    }
  }, []);

  // Saidas definitivas -> resposta: { definitive_leavings: [], start, end }
  const listHomeDefinitiveLeavings = useCallback(async (params, config = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];

    try {
      const response = await api.get('internals/internal/async/home/definitive_leavings/', {
        ...config,
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return Array.isArray(response.data?.definitive_leavings) ? response.data.definitive_leavings : [];
    } catch (error) {
      if (isCanceled(error)) throw error;
      console.error('Erro ao buscar saidas definitivas da home:', error);
      toast.error('Erro ao buscar saidas definitivas');
      return [];
    }
  }, []);

  return (
    <HomeContext.Provider
      value={{
        listHomeEntries,
        listHomeTransfers,
        listHomeSchedulings,
        listHomeQuantitativeCustody,
        listHomeQuantitativeOperational,
        listHomeTemporaryLeavings,
        listHomeDefinitiveLeavings,
      }}
    >
      {children}
    </HomeContext.Provider>
  );
};
